/**
 * Single-active-surface token holder (user-approved policy 2). The WS
 * handshake's `auth_ok` frame carries an opaque per-slot `surface`
 * token (32 lowercase-hex chars) the server minted when this socket
 * claimed the active WS slot for `(table, player)`. The client echoes
 * it back on every mutating table-scoped HTTP request via the
 * `x-surface-id` header so the server can reject a mutation from a
 * displaced/older surface (`403 session.superseded`) while the newest
 * surface keeps acting.
 *
 * Module-level (not Zustand) because the HTTP signing layer
 * (`session/authed_request.ts`) reads it synchronously per request and
 * never needs to re-render on a change — a render-store would only add
 * subscription churn. Keyed by `tableIdHex` so a table switch / a
 * second table never echoes a stale slot's token.
 *
 * Lifecycle:
 *   - `setSurfaceToken` — the WS client calls this on `auth_ok`
 *     (and re-calls it transparently on every reconnect, since a fresh
 *     slot mints a fresh token).
 *   - `clearSurfaceToken` — the WS client calls this when the socket
 *     closes as displaced / upgrade-required, so a torn-down surface
 *     stops echoing a token the server has already superseded.
 *   - `getSurfaceToken` — the HTTP layer reads the token for the table
 *     it is mutating; `null` (no live slot) means "send no header",
 *     which the BE gate treats as the back-compat allow case.
 */

let activeTableIdHex: string | null = null;
let activeToken: string | null = null;

export function setSurfaceToken(tableIdHex: string, token: string): void {
  // An empty token (a pre-policy-2 server that omits the field) is
  // treated as absent so the HTTP layer sends no header.
  if (token === "") {
    if (activeTableIdHex === tableIdHex) clearSurfaceToken();
    return;
  }
  activeTableIdHex = tableIdHex;
  activeToken = token;
}

/** Read the active surface token for `tableIdHex`, or `null` when no
 *  live slot owns it (reconnect window / different table / pre-attach).
 *  `null` => the HTTP layer omits `x-surface-id` (back-compat allow). */
export function getSurfaceToken(tableIdHex: string): string | null {
  return activeTableIdHex === tableIdHex ? activeToken : null;
}

export function clearSurfaceToken(): void {
  activeTableIdHex = null;
  activeToken = null;
}
